import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "../components/AppShell";
import { PortfolioCharts } from "../components/portfolio/PortfolioCharts";
import { PortfolioTable } from "../components/portfolio/PortfolioTable";
import { api } from "../lib/api";
import { saveBlob } from "../lib/files";
import { formatDate } from "../lib/format";

const PeriodPicker = ({ periods, value, onChange }) => (
  <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1" role="tablist" data-testid="period-selector">
    {periods.map((p) => (
      <button key={p.key} type="button" role="tab" aria-selected={value === p.key} onClick={() => onChange(p.key)}
        data-testid={`period-${p.key}`}
        className={`shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${value === p.key ? "border-[#0f5ef7] bg-[#0f5ef7] text-white" : "border-[#dfe7f3] bg-white text-[#3b475c] hover:border-[#9bb7e8]"}`}>
        {p.label}
      </button>
    ))}
  </div>
);

export default function PortfolioPage() {
  const [period, setPeriod] = useState("last12");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/portfolio/history", { params: { period } }).then((r) => setData(r.data))
      .catch(() => toast.error("No se pudo cargar el historial de la cartera"));
  }, [period]);

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/portfolio/history.xlsx", { params: { period }, responseType: "blob" });
      saveBlob(r.data, `Portfolio History - ${data?.period_label || period}.xlsx`);
    } catch {
      toast.error("No se pudo descargar el Excel");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#0f5ef7]">Portfolio</p>
          <h1 className="text-3xl font-extrabold tracking-tight text-[#182235] sm:text-4xl" data-testid="portfolio-title">Portfolio History</h1>
          <p className="mt-1 text-sm text-[#5b667a]" data-testid="portfolio-cut-date">Data as of {data?.cut_date ? formatDate(data.cut_date) : "…"}</p>
        </div>
        <button type="button" onClick={download} disabled={!data?.months?.length || busy} className="tr-btn-primary px-4 py-2.5 text-sm" data-testid="portfolio-download-excel">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download Excel
        </button>
      </div>
      {data && <PeriodPicker periods={data.periods} value={period} onChange={setPeriod} />}
      {!data ? (
        <div className="flex items-center gap-2 text-sm text-[#5b667a]" data-testid="portfolio-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : data.months.length ? (
        <>
          <PortfolioCharts months={data.months} />
          <PortfolioTable months={data.months} totals={data.totals} note={data.note} />
        </>
      ) : (
        <p className="tr-card p-6 text-sm text-[#5b667a]" data-testid="portfolio-empty">No payment data available yet.</p>
      )}
    </AppShell>
  );
}
